import React, { useCallback, useState, memo } from 'react';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import EditTagPopup from '../../components/Popup/EditTagPopup';
import { saveTag } from '../../actions/tag';
import { usePopupContext } from '../../contexts/PopupContext';

const EXCLUDE_IDS = [5, 6]; // exclude Business Model, Broad Industry Category

const FIELDS = ['category', 'tagTypeId', 'parentId'];

const isParentAvailable = tagTypeId => {
  if (!tagTypeId || isNaN(tagTypeId)) return false;

  return EXCLUDE_IDS.indexOf(parseInt(tagTypeId, 10)) < 0;
};

const EditTagPopupContainer = ({ popup, loading, types, parents, saveTag }) => {
  const { closePopup } = usePopupContext();
  const id = popup.getIn(['props', 'id'], '');
  const onSave = popup.getIn(['props', 'onSave']);
  const [formState, setFormState] = useState(() => ({
    category: popup.getIn(['props', 'category'], ''),
    parentId: popup.getIn(['props', 'parentId']) || '',
    tagTypeId: popup.getIn(['props', 'tagTypeId'], ''),
    errors: {},
  }));
  const { category, parentId, tagTypeId, errors } = formState;

  const handleChange = useCallback(({ target }) => {
    const { name, value } = target;

    setFormState(prev => ({ ...prev, [name]: value, errors: {} }));
  }, []);

  const handleCancel = useCallback(() => closePopup(), [closePopup]);

  const handleSave = useCallback(() => {
    const validationErrors = {};

    if (!category) {
      validationErrors.category = 'Category name can not be empty';
    }

    if (!tagTypeId) {
      validationErrors.tagTypeId = 'Tag type can not be empty';
    } else if (isParentAvailable(tagTypeId) && !parentId) {
      validationErrors.parentId = 'Parent category is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setFormState(prev => ({ ...prev, errors: validationErrors }));

      return;
    }

    const saveBody = {
      category,
      parentId: parentId || undefined,
      tagTypeId,
      afterSuccess: () => {
        closePopup();
        onSave();
      },
      afterError: errorPayload => {
        try {
          const apiErrors = Object.entries(errorPayload.responseBody.errors).reduce((result, [field, messages]) => {
            const message = [].concat(messages).join(' ');

            if (FIELDS.includes(field)) {
              return { ...result, [field]: message };
            }

            return { ...result, form: [...(result.form || []), message || field] };
          }, {});

          setFormState(prev => ({ ...prev, errors: apiErrors }));
        } catch (unknownError) {
          setFormState(prev => ({ ...prev, errors: { form: ['Unknown error'] } }));
        }
      },
    };

    if (id) {
      saveBody.id = id;
    }
    saveTag(saveBody);
  }, [category, tagTypeId, parentId, id, closePopup, onSave, saveTag]);

  return (
    <EditTagPopup
      category={category}
      errors={errors}
      isNew={!id}
      loading={loading}
      onCancel={handleCancel}
      onChange={handleChange}
      onSave={handleSave}
      parentId={parentId}
      parents={parents}
      showParent={isParentAvailable(tagTypeId)}
      tagTypeId={tagTypeId}
      types={types}
    />
  );
};

const mapStateToProps = state => ({
  loading: state.tags.get('loading') || state.tags.get('parentTagsLoading') || state.tags.get('typesLoading'),
  types: state.tags.get('types'),
  parents: state.tags.get('parents'),
});

export { EditTagPopupContainer };
export default connect(mapStateToProps, { saveTag }, mergeProps, connectOptions)(memo(EditTagPopupContainer));
